import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Plus, Trash2, ShieldCheck, RefreshCw, X, Lock } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLE = {
  admin:  "bg-brand-50 text-brand-700",
  member: "bg-gray-100 text-gray-700",
  viewer: "bg-gray-100 text-gray-500",
};

export default function Settings() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);     // null while loading; [] if forbidden
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setTeam(data); setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      setTeam([]);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.email} added`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add user");
    } finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!confirm(`Remove ${u.email}? This deletes their workspace and all data.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.email} removed`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    } finally { setPwBusy(false); }
  };

  return (
    <div className="p-8 space-y-6 max-w-[1000px]">
      <div>
        <h1 className="text-3xl font-display text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Account and workspace details.</p>
      </div>

      {/* Profile */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Profile</h3>
        </div>
        <div className="px-5 py-2 divide-y divide-gray-100">
          {[
            ["Name", user?.name],
            ["Email", user?.email],
            ["Role", user?.role],
            ["Workspace ID", user?.workspace_id],
            ["User ID", user?.id],
          ].map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 py-3 text-sm">
              <span className="text-xs uppercase tracking-wider font-medium text-gray-500">{k}</span>
              <span className="col-span-2 text-gray-900 font-mono text-xs break-all">{v ?? "—"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Password */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Lock className="h-4 w-4 text-brand" /> Password
          </h3>
          <p className="text-xs text-gray-500 mt-1">Change the password you use to sign in.</p>
        </div>
        <form onSubmit={changePassword} className="px-5 py-5 space-y-4 max-w-md">
          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Current password</label>
            <input
              type="password" autoComplete="current-password" required
              data-testid="pw-current-input"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">New password</label>
            <input
              type="password" autoComplete="new-password" required minLength={6}
              data-testid="pw-new-input"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none"
            />
            <p className="text-[10px] text-gray-400 mt-1">At least 6 characters.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-medium text-gray-500">Confirm new password</label>
            <input
              type="password" autoComplete="new-password" required minLength={6}
              data-testid="pw-confirm-input"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:border-brand focus:ring-1 focus:ring-brand outline-none"
            />
          </div>
          <button
            type="submit" disabled={pwBusy}
            data-testid="pw-submit-btn"
            className="btn-brand inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
          >
            {pwBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
            {pwBusy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>

      {/* Team — admin only */}
      {!forbidden && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand" /> Team
              {team?.length > 0 && <span className="text-xs text-gray-400 font-normal">· {team.length}</span>}
            </h3>
            <button data-testid="team-add-btn" onClick={() => setOpen(true)}
              className="btn-brand inline-flex items-center gap-1.5 px-3 py-1.5 text-xs">
              <Plus className="h-3.5 w-3.5" /> Add admin
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {team === null ? (
              <div className="px-5 py-6 text-sm text-gray-400 flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading team…
              </div>
            ) : team.length === 0 ? (
              <div className="px-5 py-6 text-sm text-gray-400">No team members.</div>
            ) : team.map((u) => (
              <div key={u.id} className="px-5 py-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-semibold shrink-0">
                  {(u.name || u.email || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{u.name || "—"}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded ${ROLE_STYLE[u.role] || "bg-gray-100 text-gray-600"}`}>
                  {u.role || "member"}
                </span>
                {u.id !== user?.id ? (
                  <button onClick={() => remove(u)} title="Remove"
                    className="p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <span className="text-[10px] text-gray-400 px-1.5">(you)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles guide */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Roles &amp; permissions</h3>
        </div>
        <div className="px-5 py-5 text-sm text-gray-600 space-y-2">
          <p><span className="text-brand font-semibold">admin</span> — workspace ownership, billing, full access, manage team.</p>
          <p><span className="text-gray-900 font-semibold">member</span> — create campaigns, render, publish.</p>
          <p><span className="text-gray-500 font-semibold">viewer</span> — read-only access to renders &amp; performance.</p>
        </div>
      </div>

      {/* Add admin modal — small, single-purpose, always fits */}
      {open && (
        <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="min-h-screen w-full grid place-items-center p-4">
            <form onSubmit={create} className="w-full max-w-sm bg-white rounded-2xl shadow-xl relative">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h2 className="font-display text-base text-gray-900">Add team member</h2>
                <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-900">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <input data-testid="team-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Full name (optional)"
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input data-testid="team-email" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@codira.com" autoFocus
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Temporary password</label>
                  <input data-testid="team-password" type="text" required minLength={6} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 6 characters"
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm font-mono" />
                  <p className="mt-1 text-[10px] text-gray-500">They can sign in with this and change it later.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                  <select value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3 py-2 text-sm bg-white">
                    <option value="admin">admin</option>
                    <option value="member">member</option>
                    <option value="viewer">viewer</option>
                  </select>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-white">
                  Cancel
                </button>
                <button data-testid="team-submit" type="submit" disabled={busy}
                  className="btn-brand inline-flex items-center gap-1.5 px-3 py-1.5 text-xs">
                  {busy ? <><RefreshCw className="h-3 w-3 animate-spin" /> Adding…</> : <><Plus className="h-3 w-3" /> Add</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
